import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileSettingsStore } from '@/stores/profileSettingsStore';
import { useChecklistStore } from '@/stores/checklistStore';
import { useGoServerStore } from '@/stores/goServerStore';
import { useObjectionStore } from '@/stores/objectionStore';
import {
  DASHBOARD_TOUR_FORCE_START_EVENT,
  DASHBOARD_TOUR_FORCE_START_KEY,
  REQUIRED_SCRIPT_CATEGORIES,
} from '../model/constants';
import {
  buildProgressState,
  mergeCompletedSteps,
  normalizeBusinessProfile,
  normalizeOnboardingState,
} from '../model/helpers';
import type {
  OnboardingBusinessProfile,
  OnboardingState,
} from '../model/types';
import { useOnboardingStore } from '../store/onboardingStore';

const normalizeCategoryName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[()"'`]/g, '')
    .replace(/\s+/g, ' ');

const REQUIRED_CATEGORY_ALIASES: Record<(typeof REQUIRED_SCRIPT_CATEGORIES)[number], string[]> = {
  Hook: ['hook', 'opening', 'opener', 'greeting', 'intro', 'פתיח', 'פתיחה'],
  'Key Questions': [
    'key questions',
    'questions',
    'discovery',
    'needs discovery',
    'needs',
    'qualification questions',
    'בירור',
    'בירור צרכים',
    'שאלות',
    'שאלות מפתח',
    'צרכים',
  ],
  Qualification: [
    'qualification',
    'qualifying',
    'fit',
    'criteria',
    'approval',
    'closing criteria',
    'קריטריונים',
    'אישור',
    'התאמה',
    'סגירה',
    'תקציב',
  ],
};

const matchesRequiredCategory = (categoryName: string, requiredName: (typeof REQUIRED_SCRIPT_CATEGORIES)[number]) => {
  const normalizedCategoryName = normalizeCategoryName(categoryName);
  const aliases = REQUIRED_CATEGORY_ALIASES[requiredName];

  return aliases.some((alias) => {
    const normalizedAlias = normalizeCategoryName(alias);
    return (
      normalizedCategoryName === normalizedAlias ||
      normalizedCategoryName.includes(normalizedAlias) ||
      normalizedAlias.includes(normalizedCategoryName)
    );
  });
};

export const useCenterOnboarding = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, centerId } = useAuth();
  const routeKey = `${location.pathname}${location.search}`;

  const {
    center,
    isOpen,
    isLoading,
    isSaving,
    error,
    dismissedRouteKey,
    load,
    dismiss,
    saveBusinessProfile,
    saveProgress,
    complete,
  } =
    useOnboardingStore();
  const { fetchProfile } = useProfileSettingsStore();

  const {
    categories,
    questions,
    fetchStructure,
    updateCenterDetails,
  } = useChecklistStore();
  const { objections, fetchObjections } = useObjectionStore();
  const {
    sdk,
    initializeSDK,
    setupStatus,
    setupLoading,
    checkSetupStatus,
  } = useGoServerStore();

  useEffect(() => {
    void load(centerId, user?.id ?? null, role, routeKey);
  }, [centerId, load, role, routeKey, user?.id]);

  useEffect(() => {
    if (!isOpen || !centerId) {
      return;
    }

    void fetchStructure(centerId);
    void fetchObjections(centerId);
  }, [centerId, fetchObjections, fetchStructure, isOpen]);

  const businessProfile = useMemo(
    () => normalizeBusinessProfile(center?.onboarding_business_profile),
    [center?.onboarding_business_profile]
  );
  const onboardingState = useMemo(
    () => normalizeOnboardingState(center?.onboarding_state),
    [center?.onboarding_state]
  );

  const currentStep = onboardingState.currentStep;
  const completedSteps = onboardingState.completedSteps;

  const scriptValidation = useMemo(() => {
    const requiredCategoryIds = REQUIRED_SCRIPT_CATEGORIES.map((requiredName) => {
      const category = categories.find(
        (item) => matchesRequiredCategory(item.name, requiredName)
      );

      return category?.id ?? null;
    });

    const populatedCategoryIds = categories
      .filter((category) =>
        questions.some((question) => question.category_id === category.id && question.text.trim().length > 0)
      )
      .map((category) => category.id);

    const missingCategories = requiredCategoryIds.some((categoryId) => categoryId === null);
    const hasQuestionsInEachCategory = requiredCategoryIds.every((categoryId) =>
      categoryId ? questions.some((question) => question.category_id === categoryId && question.text.trim().length > 0) : false
    );
    const fallbackComplete = populatedCategoryIds.length >= 3;

    return {
      missingCategories,
      hasQuestionsInEachCategory,
      isComplete: (!missingCategories && hasQuestionsInEachCategory) || fallbackComplete,
    };
  }, [categories, questions]);

  const objectionStepComplete = useMemo(
    () => objections.some((objection) => objection.response.trim().length > 0),
    [objections]
  );

  useEffect(() => {
    if (!isOpen || currentStep !== 1 || sdk) {
      return;
    }

    initializeSDK();
  }, [currentStep, initializeSDK, isOpen, sdk]);

  const persistStep = async (nextState: OnboardingState) => {
    if (!centerId || !user?.id) {
      return;
    }

    await saveProgress(centerId, user.id, nextState);
  };

  const goToStep = async (step: OnboardingState['currentStep']) => {
    if (step === currentStep || !centerId) {
      return;
    }

    const highestOpenStep = Math.max(currentStep, ...completedSteps, 1);
    if (step > highestOpenStep) {
      return;
    }

    await persistStep({
      ...onboardingState,
      currentStep: step,
      completedSteps,
      lastSavedAt: new Date().toISOString(),
    });
  };

  const saveAndExit = async () => {
    if (centerId) {
      await persistStep({
        ...onboardingState,
        lastSavedAt: new Date().toISOString(),
      });
    }

    dismiss(routeKey);
  };

  const saveBusinessProfileStep = async (nextBusinessProfile: OnboardingBusinessProfile) => {
    if (!centerId || !user?.id) {
      throw new Error('Missing center context for onboarding');
    }

    const nextState = buildProgressState(onboardingState, 5, 2, user?.id ?? null);
    await saveBusinessProfile(centerId, user.id, nextBusinessProfile, nextState);
  };

  const saveBusinessProfileDraft = async (draftBusinessProfile: OnboardingBusinessProfile) => {
    if (!centerId || !user?.id) {
      return;
    }

    await saveBusinessProfile(centerId, user.id, draftBusinessProfile, {
      ...onboardingState,
      lastSavedAt: new Date().toISOString(),
    });
  };

  const saveBusinessInfoDetailsStep = async (description: string) => {
    if (!centerId) {
      throw new Error('Missing center context for onboarding');
    }

    await updateCenterDetails(centerId, description);
    await finishOnboarding();
  };

  const saveScriptStep = async () => {
    await persistStep(buildProgressState(onboardingState, 5, 4, user?.id ?? null));
  };

  const completeAudioSetupStep = async () => {
    await persistStep(buildProgressState(onboardingState, 2, 1, user?.id ?? null));
  };

  const finishOnboarding = async () => {
    if (!centerId || !user?.id) {
      return;
    }

    const completedAt = new Date().toISOString();
    console.info('[Onboarding] Completing onboarding and handing off to the dashboard flow');
    await complete(centerId, user.id, {
      ...onboardingState,
      currentStep: 5,
      completedSteps: mergeCompletedSteps(onboardingState.completedSteps, 5),
      isComplete: true,
      completedAt,
      completedByUserId: user?.id ?? null,
      lastSavedAt: completedAt,
    });

    await fetchProfile(user.id);
    localStorage.setItem(DASHBOARD_TOUR_FORCE_START_KEY, 'true');
    navigate('/', { replace: true });

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(DASHBOARD_TOUR_FORCE_START_EVENT));
    }, 80);
  };

  return {
    center,
    isOpen,
    isLoading,
    isSaving,
    error,
    dismissedRouteKey,
    businessProfile,
    onboardingState,
    currentStep,
    completedSteps,
    setupStatus,
    setupLoading,
    checkSetupStatus,
    scriptValidation,
    objectionStepComplete,
    saveAndExit,
    goToStep,
    saveBusinessProfileStep,
    saveBusinessProfileDraft,
    saveScriptStep,
    saveBusinessInfoDetailsStep,
    completeAudioSetupStep,
    finishOnboarding,
  };
};
